/**
 * The Overview's row logic, as plain functions.
 *
 * ── WHAT LIVES HERE AND WHAT DOES NOT ──
 * These decide WHICH rows count and HOW MUCH of a bar each gets. They do not
 * add money: every sum the page shows either arrives from the API or goes
 * through `@fint/money` in the component. Keeping the arithmetic out is what
 * lets this file run under Node's test runner - `@fint/money`'s ESM build has
 * extensionless imports, which Vite resolves and Node does not, the same
 * reason `tokens.test.ts` reaches for `createRequire`.
 *
 * ── THE CONTRACT ──
 * GET /v1/practice/overview. Money is decimal STRINGS. A business where this
 * person lacks report permission comes back with NULL figures and a caveat
 * naming it; that is a row, not a missing row, and it renders as dashes with
 * a footnote rather than as zeros - "you may not see this" and "nothing
 * happened" are different facts.
 */

export interface OverviewOrgRow {
  orgId: string;
  name: string;
  /** The book's palette token. Optional: a response from before the field still renders. */
  colour?: string;
  gstRegime: 'gst' | 'none';
  income: string | null;
  expense: string | null;
  profit: string | null;
  receivables: string | null;
  overdue: string | null;
  invoiceCount: number | null;
  /**
   * Read if present, and the API does send both. `inTotals` is the server's
   * own word on whether this row is inside `totals`; `currency` is what the
   * row's figures are in. Absent - a response from before the fields - the
   * row is taken to be in the response's currency and in the totals whenever
   * it has figures. Typed optional so the page cannot depend on them being
   * there and go blank when they are not.
   */
  currency?: string;
  inTotals?: boolean;
  /** Of this row's figures, what is money between the person's own books. */
  betweenBooks?: { income: string; expense: string } | null;
}

export interface OverviewResponse {
  period: { from: string; to: string };
  basis: 'accrual' | 'cash';
  orgs: OverviewOrgRow[];
  /**
   * Net of money between the books - what one paid another is neither income
   * nor expense of the whole. `betweenBooks` is what was taken out, so the
   * rows still visibly add up: rows = total + betweenBooks. Optional for a
   * response from before the field.
   */
  totals: { income: string; expense: string; profit: string; betweenBooks?: { income: string; expense: string } };
  currency: string;
  caveats: string[];
}

/** A row whose figures are all present. */
export type FiguredRow = OverviewOrgRow & {
  income: string;
  expense: string;
  profit: string;
  receivables: string;
  overdue: string;
};

/**
 * Whether the row has figures at all. One null means the API withheld the
 * business - report permission is per org, and a partial row would be a
 * partial truth presented as a whole one.
 */
export function hasFigures(row: OverviewOrgRow): row is FiguredRow {
  return (
    row.income !== null &&
    row.expense !== null &&
    row.profit !== null &&
    row.receivables !== null &&
    row.overdue !== null
  );
}

/**
 * The rows the client-side column totals may add together: those with figures,
 * in the currency the response is summed in. A row in another currency is
 * still SHOWN - in its own currency - but is not added, because no rate has
 * been applied and adding rupees to dirhams produces a number that is neither.
 */
export function rowsInTotal(rows: OverviewOrgRow[], currency: string): FiguredRow[] {
  return rows.filter(
    (row): row is FiguredRow =>
      hasFigures(row) && (row.inTotals ?? (row.currency ?? currency) === currency),
  );
}

/**
 * Each magnitude's share of the whole, for a ranked bar's percentage label.
 *
 * Integers in, ratios out: these are minor units already parsed by
 * `@fint/money`, and a share is a proportion rather than an amount, so the
 * division is not money arithmetic. Signs are dropped on purpose - a loss of
 * 30,000 beside a profit of 70,000 is 30% of the movement, and a chart that
 * netted them to 40,000 would be answering a question nobody asked. All zero
 * means no shares, not NaN.
 */
export function magnitudeShares(minors: number[]): number[] {
  const total = minors.reduce((sum, minor) => sum + Math.abs(minor), 0);
  if (total === 0) return minors.map(() => 0);
  return minors.map((minor) => Math.abs(minor) / total);
}

/**
 * The bar colour for a signed profit. Palette slots rather than raw colours,
 * so both themes stay measured; and the colour is never the only carrier - the
 * row's value is rendered signed beside it and the name says "loss".
 */
export function profitColour(minor: number): 'green' | 'red' | 'slate' {
  if (minor > 0) return 'green';
  if (minor < 0) return 'red';
  return 'slate';
}

/** The bar's label: the business, and the word when the number is negative. */
export function profitLabel(name: string, minor: number): string {
  return minor < 0 ? `${name} (loss)` : name;
}

/**
 * Where a person lands after signing in.
 *
 * More than one business: the Overview, because the first question is "how
 * are they all doing" and the per-org dashboard can only answer for one.
 * Exactly one: that business's dashboard, as it always was - an overview of
 * one thing is a dashboard with a worse name.
 */
export function landingPath(orgCount: number): '/' | '/overview' {
  return orgCount > 1 ? '/overview' : '/';
}
